import { chunk, merge } from 'lodash-es';
import { mapState, mapActions } from 'vuex';
import promotions from '@core/mixins/promotions.js';
import { floorDiscountedPrice } from '@utils/pricing.ts';

/**
 * Check if any of the given license IDs are covered by the promo.
 * Supports both license_ids array and discounts[].list fallback.
 * @param {Object} promo - promotion object
 * @param {number[]} ids - array of license IDs
 */
export function isPromoLicenseMatch(promo, ids) {
    const promoIDs = promo?.license_ids || [];
    if (promoIDs.length) return promoIDs.some((id) => ids.includes(id));

    const discounts = promo?.discounts || [];
    return discounts.some((item) => item.list?.some((id) => ids.includes(id)));
}

export default {
    mixins: [promotions],

    computed: {
        ...mapState({
            licensesItems: (state) => state.licenses.data,
            productItems: (state) => state.products.items,
            internalProductId: (state) => state.pages?.page?.product_id || null,
        }),
    },

    methods: {
        ...mapActions({
            generateSessionURL: 'cleverbridge/get',
        }),

        findCleverBridgeCart(slice, settings) {
            const licenses = this.recursiveSearch(slice || {}, 'cleverBridgeCartID').flat(2);
            const cleverBridgeCartList = licenses.map((license) => license.cleverBridgeCartID).flat(2);
            return this.getLicenses({ ...settings, cleverBridgeCartList });
        },

        productCTA(settings) {
            const type = settings?.isPurchase ? 'purchasePageCTA' : 'productPageCTA';
            const productId = settings?.productId || this.internalProductId;
            return this.productItems[productId]?.parameters?.common?.[type] || {};
        },

        async getLicenses(settings) {
            // The currency of the country selected by the user should be used.
            const locale = settings?.locale || this.$store.state.geolocation.desiredLocale || this.$store.state.geolocation.requestedLocale;

            await this.$store.dispatch('locales/getLocaleData', { locale });

            const PAGINATE_SIZE = 50;
            const payload = {
                locale,
                currency: this.productCurrency,
                paginate: { number: 1, size: PAGINATE_SIZE },
            };

            const pageSettingsCTA = this.productCTA(settings);

            const license = merge({}, pageSettingsCTA, settings);
            const licenses = license?.cleverBridgeCartList || [license?.cleverBridgeCartID] || [];

            if (licenses.length === 1) {
                const cleverBridgeCartID = licenses[0];
                const productId = this.settings?.productId || this.internalProductId;

                // The promotion can be added locally in the page settings
                const promoLocal = this.findPromotion(productId, [cleverBridgeCartID]);
                const promoLocalLicenseID = Number(promoLocal.license_id_override) || promoLocal.promoSettings?.product_page_license_id;

                // or globally in the promotions section
                const promoGlobal = this.promotionList.find((item) => item.product_id === productId) || {};
                const promoGlobalLicenseID = Number(promoGlobal.license_id_override) || promoGlobal.promoSettings?.product_page_license_id;

                licenses.push(promoLocalLicenseID || promoGlobalLicenseID);
            }

            const promisesWithOutPromo = chunk(licenses.filter(Number), PAGINATE_SIZE)
                .map((cleverbridgeIDs) => this.$store.dispatch('licenses/getList', { ...payload, cleverbridgeIDs }));

            await Promise.all(promisesWithOutPromo);
        },

        licenseData({ cleverBridgeCartID, settingsCTA }) {
            const resultDefault = { price: 0, price_promo: 0 };
            const cleverbBridgeID = (cleverBridgeCartID || settingsCTA?.cleverBridgeCartID || '').toString();
            if (!cleverbBridgeID) return resultDefault;

            const compare = (i) => i.cleverbridge_id === cleverbBridgeID && i.currency === this.productCurrency;
            const license = this.licensesItems.find(compare) || resultDefault;
            const productId = license.product_id;
            const promotionActive = this.findPromotion(productId, [cleverBridgeCartID]);
            const promotion = merge({}, promotionActive, settingsCTA);
            const discounts = promotion.discounts || [];
            const promotionItem = discounts.find((item) => item.list.includes(cleverBridgeCartID)) || {};
            const discount = discounts.length
                ? (promotionItem.discount || 0)
                : (promotion.discount || 0);

            if (discount) {
                return {
                    ...license,
                    ...promotion,
                    discount,
                    cleverbridge_id: cleverBridgeCartID,
                    price_promo: floorDiscountedPrice(license.price, discount),
                };
            }

            return license;
        },

        setPromo() {
            const productId = this.purchasePageCTA?.productId || this.internalProductId;
            const cleverBridgeCartIDs = this.licensesItems.map((item) => Number(item.cleverbridge_id));
            const promotionActive = this.findPromotion(productId, cleverBridgeCartIDs);

            this.promotion = merge({}, promotionActive, this.purchasePageCTA);

            const hasDiscount = this.promotion.discount || this.promotion.discounts?.length;
            this.promotion.isActive = Boolean(hasDiscount && !this.promotion?.doNotApplyPromo);

            if (!this.promotion.isActive) return;
            if (!this.products?.items) return;

            this.products.selected = Number(this.promotion?.selected_licenses_count || 0);

            this.products.items = this.products.items.map((item) => ({
                ...item,
                pc: {
                    ...item.pc,
                    selected: Number(this.promotion?.selected_computers_count || 0),
                },
                cloud: {
                    ...item.cloud,
                    selected: Number(this.promotion?.[`default_cloud_capacity_${item.id}`] || 0),
                },
            }));
        },

        isPromoLicenseMatch,

        initSettingsCTA(list) {
            const settings = (list || []).filter((item) => item?.productPageCTA?.productId);
            return settings.map((product) => this.getLicenses(product.productPageCTA));
        },

        recursiveSearch(obj, searchKey, results = []) {
            Object.keys(obj || {}).forEach((key) => {
                if (key === searchKey) {
                    results.push(obj);
                    return;
                }
                if (typeof obj[key] === 'object') {
                    this.recursiveSearch(obj[key], searchKey, results);
                }
            });
            return results;
        },
    },
};
